<div class="ops-dashboard">

    <!-- ============================== Header ============================== -->
    <header class="ops-header">
        <div class="title-block">
            <h1>Operations Dashboard</h1>
            <p class="sub">Manager view — run the business today</p>
        </div>
    </header>

    <!-- ============================== Filter Bar ============================== -->
    <section class="ops-filter-bar">
        <div class="filter-group">
            <label class="kbma-label">Range</label>
            <div class="preset-chips">
                <button *ngFor="let p of presets"
                        class="chip"
                        [class.chip--active]="selectedPreset === p.key"
                        (click)="applyPreset(p.key)">
                    {{ p.label }}
                </button>
            </div>
        </div>

        <div class="filter-group" *ngIf="selectedPreset === 'custom'">
            <label class="kbma-label">From</label>
            <p-calendar [(ngModel)]="startDate" (onSelect)="onCustomRangeChange()" dateFormat="mm/dd/yy" styleClass="ops-date-picker" appendTo="body"></p-calendar>
        </div>
        <div class="filter-group" *ngIf="selectedPreset === 'custom'">
            <label class="kbma-label">To</label>
            <p-calendar [(ngModel)]="endDate" (onSelect)="onCustomRangeChange()" dateFormat="mm/dd/yy" styleClass="ops-date-picker" appendTo="body"></p-calendar>
        </div>

        <div class="filter-group">
            <label class="kbma-label">Location</label>
            <select class="kbma-select" [(ngModel)]="selectedLocationId" (ngModelChange)="onLocationChange()">
                <option *ngFor="let loc of locationOptions" [ngValue]="loc.id">{{ loc.name }}</option>
            </select>
        </div>

        <div class="filter-group filter-toggle">
            <label class="kbma-label">
                <input type="checkbox" [(ngModel)]="compareWow" (ngModelChange)="onCompareWowChange()" />
                Compare WoW
            </label>
        </div>

        <div class="filter-group filter-actions">
            <button class="kbma-btn kbma-btn--secondary kbma-btn--sm" (click)="load()" [disabled]="loading">
                Refresh
            </button>
        </div>
    </section>

    <!-- Loading / error states. The loading banner shrinks to a thin strip
         while sections are still arriving (instead of hiding the page),
         and disappears once everything is done. Each section below has its
         own *ngIf="data?.<name>" guard, so sections paint progressively as
         their HTTP responses arrive. -->
    <div *ngIf="loading" class="ops-loading ops-loading--inline">
        Loading
        (<span class="numeric">{{ inFlightCount }}</span> of <span class="numeric">{{ sectionNames.length }}</span> sections pending)…
    </div>
    <div *ngIf="drilldownLoading && !drilldownPanelOpen" class="ops-loading ops-loading--inline">
        Fetching drill-down…
    </div>

    <!-- Drill-down moved to its own route /operations-dashboard/drilldown.
         drilldown() in the dashboard fetches, then navigate()s to the new
         page with rows in router state. The new page mounts fresh with all
         data pre-loaded — no post-mount transitions. -->

    <div *ngIf="error && !loading" class="ops-error">
        <strong>Error:</strong> <span>{{ error }}</span>
    </div>

    <ng-container *ngIf="data">

        <!-- ============================== Alert Banner ============================== -->
        <div class="ops-alert-banner" *ngIf="data.alerts && data.alerts.length">
            <div class="alert-row" *ngFor="let a of data.alerts">
                <span class="kbma-badge" [ngClass]="severityClass(a.severity)">{{ a.severity }}</span>
                <span class="alert-message">{{ a.message }}</span>
            </div>
        </div>

        <!-- ============================== §1 KPI Strip ============================== -->
        <!--
            Layout per manager request: 7 primary KPIs in row 1 in the order
            Arrivals · Departures · Occupancy · Rooms Occupied · Guests on Property
            · ADR · RevPAR. Row 2 holds Futons / Rollaways (with 30-day revenue).
            (Data Health tile was also in row 2 originally; removed per a later
            manager request — backend still returns data.dataHealth.)

            Removed from the strip: Occupancy Yesterday, Occupancy MTD, Vacant
            (Clean), Out of Order. The backend still returns those fields
            (occupancyYesterday, occupancyMtd, roomsVacant, roomsOutOfOrder) so
            they can be reinstated without API work if needed.
        -->
        <section class="ops-section kpi-strip">
            <!-- 1. Arrivals. Clicking opens the Arrivals Agent board in a new tab.
                 The board is single-day; for a multi-day dashboard range we pick the
                 most actionable day (today if in range, else startDate for forward
                 ranges, else endDate for past ranges) — see openArrivalsAgentForTile. -->
            <div class="kbma-kpi kbma-kpi--info kbma-kpi--clickable" (click)="openArrivalsAgentForTile('arrivals')">
                <div class="kbma-kpi__label">Arrivals</div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.kpis?.arrivalsToday) }}</span></div>
                <!-- Early Check-In count — # of arrivals carrying an
                     Arrival Status tag (Early Check-In 9AM..3PM). 0 means
                     all arrivals are on the standard 4 PM check-in. -->
                <div class="kbma-kpi__delta" *ngIf="(data.kpis?.arrivalsEarlyCheckIn || 0) > 0"
                     pTooltip="Number of today's arrivals with an Early Check-In tag set (9AM / 10AM / 11AM / 12PM / 1PM / 2PM / 3PM). The rest are on the standard 4:00 PM check-in."
                     tooltipPosition="top">
                    <span class="numeric">{{ fmtNum(data.kpis?.arrivalsEarlyCheckIn) }}</span> early
                </div>
                <div class="kbma-kpi__wow" *ngIf="data.kpisLastWeek">
                    Last wk: <span class="numeric">{{ fmtNum(data.kpisLastWeek?.arrivalsToday) }}</span>
                </div>
            </div>

            <!-- 2. Departures — opens /reservations filtered by checkOutDate
                 over the dashboard's range. status filter matches the
                 dashboard's count predicate (OP/OC/OG/OO). -->
            <div class="kbma-kpi kbma-kpi--info kbma-kpi--clickable" (click)="openReservationsForDepartures()">
                <div class="kbma-kpi__label">Departures</div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.kpis?.departuresToday) }}</span></div>
                <!-- Late Checkout count — # of departures carrying a Departure
                     Status tag (Late Checkout 12PM..8PM). 0 means all
                     departures are on the standard 11 AM checkout. -->
                <div class="kbma-kpi__delta" *ngIf="(data.kpis?.departuresLateCheckout || 0) > 0"
                     pTooltip="Number of today's departures with a Late Checkout tag set (12PM / 1PM / 2PM / 3PM / 4PM / 5PM / 6PM / 7PM / 8PM). The rest are on the standard 11:00 AM checkout."
                     tooltipPosition="top">
                    <span class="numeric">{{ fmtNum(data.kpis?.departuresLateCheckout) }}</span> late
                </div>
                <div class="kbma-kpi__wow" *ngIf="data.kpisLastWeek">
                    Last wk: <span class="numeric">{{ fmtNum(data.kpisLastWeek?.departuresToday) }}</span>
                </div>
            </div>

            <!-- 3. Occupancy. Clicking opens the full /reservations list with the
                 same scope as the tile (status OP/NA/OO/OG spanning the selected
                 range, current location filter applied) — manager can then work
                 the list with the full reservations UI. -->
            <div class="kbma-kpi kbma-kpi--info kbma-kpi--clickable" (click)="openOccupancyReservations()">
                <div class="kbma-kpi__label">Occupancy</div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtPct(data.kpis?.occupancyToday) }}</span></div>
                <div class="kbma-kpi__delta">
                    <span [ngClass]="deltaClass(data.kpis?.occupancyDeltaWoW)">{{ fmtDelta(data.kpis?.occupancyDeltaWoW) }}</span>
                </div>
                <div class="kbma-kpi__wow" *ngIf="data.kpisLastWeek">
                    Last wk: <span class="numeric">{{ fmtPct(data.kpisLastWeek?.occupancyToday) }}</span>
                </div>
            </div>

            <!-- 4. Rooms Occupied — opens /reservations with the SAME predicate
                 the tile counts (status OP/NA/OO/OG, stay overlaps range,
                 listingType=1 real units only). Manager uses the existing
                 reservation list UI, not a proprietary drilldown. -->
            <div class="kbma-kpi kbma-kpi--success kbma-kpi--clickable" (click)="openOccupancyReservations()">
                <div class="kbma-kpi__label">Rooms Occupied</div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.kpis?.roomsOccupied) }}</span></div>
                <div class="kbma-kpi__delta">of <span class="numeric">{{ fmtNum(data.kpis?.roomsTotal) }}</span> total</div>
            </div>

            <!-- 5. Guests on Property — same /reservations route as Rooms Occupied.
                 The "guests" capacity proxy is computed from the same reservation
                 set, so the row count on /reservations equals the tile's
                 underlying reservation count. -->
            <div class="kbma-kpi kbma-kpi--info kbma-kpi--clickable" (click)="openOccupancyReservations()">
                <div class="kbma-kpi__label">Guests on Property</div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.kpis?.guestsOnProperty) }}</span></div>
                <!-- Early Check-In / Late Checkout tag counts across the
                     in-house cohort. Surfaces operational load even on a day
                     with no arrivals/departures (e.g. mid-stay guests with
                     pre-arranged late checkouts tomorrow). -->
                <div class="kbma-kpi__delta"
                     *ngIf="(data.kpis?.guestsEarlyCheckIn || 0) > 0 || (data.kpis?.guestsLateCheckout || 0) > 0"
                     pTooltip="In-house reservations whose Arrival Status / Departure Status tag is set. 'Early' = guests on a non-standard early check-in (9AM..3PM); 'Late' = guests on a non-standard late checkout (12PM..8PM)."
                     tooltipPosition="top">
                    <span *ngIf="(data.kpis?.guestsEarlyCheckIn || 0) > 0">
                        <span class="numeric">{{ fmtNum(data.kpis?.guestsEarlyCheckIn) }}</span> early
                    </span>
                    <span *ngIf="(data.kpis?.guestsEarlyCheckIn || 0) > 0 && (data.kpis?.guestsLateCheckout || 0) > 0"> · </span>
                    <span *ngIf="(data.kpis?.guestsLateCheckout || 0) > 0">
                        <span class="numeric">{{ fmtNum(data.kpis?.guestsLateCheckout) }}</span> late
                    </span>
                </div>
                <div class="kbma-kpi__delta" *ngIf="data.kpis?.guestsEstimated">est.</div>
            </div>

            <!-- 6. ADR — Trailing 30-day arrival cohort.
                 Sums room revenue + stay-nights for every reservation whose
                 CHECK-IN falls within the trailing 30 days ending at the
                 dashboard's endDate. NOT in-house tonight, NOT arrivals
                 today only — see the tooltip for the full predicate. -->
            <div class="kbma-kpi kbma-kpi--success">
                <div class="kbma-kpi__label">
                    ADR (Trailing 30d)
                    <i class="pi pi-info-circle kbma-kpi__info"
                       pTooltip="Average Daily Rate over the TRAILING 30-DAY ARRIVAL COHORT.

Numerator: SUM of nightly-rate line items (orderProductId = 1) from str_order_detail.
Denominator: SUM of numberOfNightsStay across the same reservations.

Reservation set: every reservation whose CHECK-IN date falls within the trailing 30 days ending at the dashboard's selected end date, with status OP (Reserved) or OC (Sale Closed). Excludes OG / OO (owner stays), NA (maintenance), and all non-room line items.

NOT 'in-house tonight' — a guest who checked in 28 days ago for 7 nights is included in full. NOT 'arrivals today only' — that's a single-day cohort. This is the standard hospitality trailing ADR."
                       tooltipPosition="top"
                       tooltipStyleClass="ops-tooltip"></i>
                </div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtCurrency(data.kpis?.adr30d) }}</span></div>
                <div class="kbma-kpi__delta">last 30d arrival cohort</div>
            </div>

            <!-- 7. RevPAR — same numerator as ADR, denominator = active units × 30. -->
            <div class="kbma-kpi kbma-kpi--success">
                <div class="kbma-kpi__label">
                    RevPAR (Trailing 30d)
                    <i class="pi pi-info-circle kbma-kpi__info"
                       pTooltip="Revenue Per Available Room over the TRAILING 30-DAY ARRIVAL COHORT.

Numerator: SAME room revenue as ADR (orderProductId = 1, OP / OC reservations whose check-in falls inside the trailing 30 days).
Denominator: active units × 30 (whether sold or not).

Industry-standard yield metric — measures how well the inventory monetizes regardless of whether nights are sold. RevPAR ≤ ADR always; the gap widens as occupancy falls."
                       tooltipPosition="top"
                       tooltipStyleClass="ops-tooltip"></i>
                </div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtCurrency(data.kpis?.revpar30d) }}</span></div>
                <div class="kbma-kpi__delta">last 30d arrival cohort</div>
            </div>
        </section>

        <!-- ====================== Equipment + Health row ====================== -->
        <!-- 30d revenue figures pulled from data.equipment.{futon,rollaway}.revenue30d
             (sourced from str_order_detail line totals; same number shown on the
             dedicated equipment card below). -->
        <section class="ops-section kpi-strip-secondary">
            <!-- Headline value = quantity of futons sold over the trailing 30 days,
                 restricted to orders whose invoice has totalPaid > 0 ("who paid for
                 the futon"). Sub-label = revenue from the same paid set. Click drills
                 into the underlying line items via drilldownEquipmentRevenue, which
                 applies the identical paid-invoice filter — so the row count matches
                 the headline exactly. (Pool / Available counts removed per manager
                 request — utilization badge on the §2 card still shows "% in use".) -->
            <div class="kbma-kpi kbma-kpi--warning kbma-kpi--clickable" (click)="drilldown('futonsRevenue', 'Futons Sold — Paid Line Items (30d)')">
                <div class="kbma-kpi__label">Futons Sold (30d)</div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.kpis?.futonsSold30d) }}</span></div>
                <div class="kbma-kpi__delta">30d rev <span class="numeric">{{ fmtCurrency(data.kpis?.futonsRevenue30dPaid) }}</span></div>
            </div>

            <div class="kbma-kpi kbma-kpi--warning kbma-kpi--clickable" (click)="drilldown('rollawaysRevenue', 'Rollaways Sold — Paid Line Items (30d)')">
                <div class="kbma-kpi__label">Rollaways Sold (30d)</div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.kpis?.rollawaysSold30d) }}</span></div>
                <div class="kbma-kpi__delta">30d rev <span class="numeric">{{ fmtCurrency(data.kpis?.rollawaysRevenue30dPaid) }}</span></div>
            </div>
            <!-- Data Health KPI tile + §5 Data Health card removed per manager
                 request. Backend still exposes data.dataHealth in the API
                 payload (Breezeway / VRBO push status, OTA channel sync,
                 integrity counts) — uncomment this tile + the §5 section
                 below to reinstate without any backend change. -->
        </section>

        <!-- ============================== Per-Location Operations Table ==============================
             Moved to be the first breakdown after the tiles per manager request — gives a
             location-by-location snapshot before any deeper section. The <tfoot> row aggregates
             every column, including the shared-pool-aware futon total (see locationTotals() in
             operations-dashboard.component.ts). -->
        <section class="ops-section">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>Operations by Location</h3>
                    <!-- Every numeric column on this table is scoped to the dashboard's
                         selected date range — defaults to TODAY. Arrivals / Departures
                         use the range as a check-in / check-out window; Occupied /
                         Occupancy / Guests / Futons / Rollaways / Housekeeping Mix are
                         point-in-time snapshots as of the range's END date. -->
                    <span class="caption">
                        Scoped to selected range
                        <strong>{{ startDate | date:'MM/dd/yy' }}</strong>
                        <ng-container *ngIf="(startDate | date:'MM/dd/yy') !== (endDate | date:'MM/dd/yy')">
                            → <strong>{{ endDate | date:'MM/dd/yy' }}</strong>
                        </ng-container>
                        · Arrivals / Departures = range; Occupied / Guests / Mix = snapshot at end date
                    </span>
                    <!-- Mix-bar colour legend. mixBarStyle() reads colours from
                         OperationsDashboardComponent.COLOR; the four hex codes
                         here mirror that map so legend chips and bar segments
                         can't drift apart. -->
                    <span class="hk-mix-legend">
                        <span class="legend-item"><span class="legend-swatch" style="background:#8B0000"></span>Dirty</span>
                        <span class="legend-item"><span class="legend-swatch" style="background:#FF69B4"></span>Housekeeping</span>
                        <span class="legend-item"><span class="legend-swatch" style="background:#9333EA"></span>Inspection</span>
                        <span class="legend-item"><span class="legend-swatch" style="background:#10B981"></span>Ready</span>
                    </span>
                </div>
                <table class="kbma-table">
                    <thead>
                        <tr>
                            <th>Location</th>
                            <th class="numeric">
                                Total
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Active short-term-rental units in this location — listingStatus = Active and listingType = Listed (child unit, not a composite/parent). This is the denominator for the Occupancy column."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Occupied
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Distinct units with at least one canonical-occupancy reservation (status OP / NA / OO / OG) spanning the selected day. Same definition as the Rooms Occupied tile and the Occupancy % numerator — row totals sum to the tile."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Occupancy
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Occupied ÷ Total for this location. Uses the same OP/NA/OO/OG set as the top-of-page Occupancy % tile, so the per-location values are consistent with the headline number."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Arrivals
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Reservations checking in within the selected date range. Excludes composite parent listings (listingType = 2) and Hold reservations (reservationType = 14) — same filters as the Arrivals Agent board."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Departures
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Reservations checking out within the selected date range. Same composite / Hold exclusions as Arrivals."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Guests
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Estimated guests on the unit's reservations spanning the selected day, using a per-bedroom capacity proxy (bedrooms × 2). Composite parent listings (listingType = 2) are excluded so a composite + its child units aren't double-counted. The tile shows the same value flagged 'est.' to signal the proxy."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Futons
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Count of futons currently out at this location today. Click the cell to drill into the futon line items."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Rollaways
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Count of rollaways currently out at this location today. Click the cell to drill into the rollaway line items."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th>
                                Housekeeping Mix
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Stacked bar of the unit count by physical room status (Breezeway / RoomStatusEnum): DIRTY · HOUSEKEEPING · INSPECTION (ready-for-inspection + inspection) · CLEAN_READY. Click a segment to drill into the units in that status."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let loc of data.locations">
                            <td>{{ loc.locationName }}</td>
                            <td class="numeric cell-clickable" (click)="cellDrill('total',        loc.locationId)"><span class="numeric">{{ fmtNum(loc.totalUnits) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('occupied',     loc.locationId)"><span class="numeric">{{ fmtNum(loc.occupied) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('occupancyPct', loc.locationId)"><span class="numeric">{{ fmtPct(loc.occupancyPct) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('arrivals',     loc.locationId)"><span class="numeric">{{ fmtNum(loc.arrivals) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('departures',   loc.locationId)"><span class="numeric">{{ fmtNum(loc.departures) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('guests',       loc.locationId)"><span class="numeric">{{ fmtNum(loc.guests) }}</span></td>
                            <!-- Pool/available denominators removed per manager request — just
                                 show the count of futons/rollaways out for the day. -->
                            <td class="numeric cell-clickable" (click)="cellDrill('futonsOut',    loc.locationId)"><span class="numeric">{{ fmtNum(loc.futonsOut) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('rollawaysOut', loc.locationId)"><span class="numeric">{{ fmtNum(loc.rollawaysOut) }}</span></td>
                            <td>
                                <!-- Unit count is rendered inline inside each segment now. The text
                                     is white with a subtle shadow so it stays readable across all
                                     four colours; segments narrower than ~16px auto-truncate via
                                     overflow:hidden. Falsy values (0 / null) render as empty so
                                     zero-width segments don't display a stray "0". -->
                                <div class="hk-mix-bar" *ngIf="!isHkMixIdle(loc)">
                                    <div class="mix-seg mix-seg--clickable" [ngStyle]="mixBarStyle(loc, 'dirty')"
                                         (click)="drilldown('unitsDirty', 'Dirty Units', loc.locationId); $event.stopPropagation();"
                                         [title]="'Dirty: ' + (loc.housekeepingMix?.dirty || 0) + ' — click to see units'">{{ loc.housekeepingMix?.dirty || '' }}</div>
                                    <div class="mix-seg mix-seg--clickable" [ngStyle]="mixBarStyle(loc, 'housekeeping')"
                                         (click)="drilldown('unitsHousekeeping', 'Units in Housekeeping', loc.locationId); $event.stopPropagation();"
                                         [title]="'Housekeeping: ' + (loc.housekeepingMix?.housekeeping || 0) + ' — click to see units'">{{ loc.housekeepingMix?.housekeeping || '' }}</div>
                                    <div class="mix-seg mix-seg--clickable" [ngStyle]="mixBarStyle(loc, 'inspection')"
                                         (click)="drilldown('unitsInspection', 'Units in Inspection', loc.locationId); $event.stopPropagation();"
                                         [title]="'Inspection: ' + (loc.housekeepingMix?.inspection || 0) + ' — click to see units'">{{ loc.housekeepingMix?.inspection || '' }}</div>
                                    <div class="mix-seg mix-seg--clickable" [ngStyle]="mixBarStyle(loc, 'ready')"
                                         (click)="drilldown('vacantClean', 'Ready Units', loc.locationId); $event.stopPropagation();"
                                         [title]="'Ready: ' + (loc.housekeepingMix?.ready || 0) + ' — click to see units'">{{ loc.housekeepingMix?.ready || '' }}</div>
                                </div>
                                <!-- Idle state — nothing in the cleaning lifecycle. Rendered as
                                     structured spans so the "X unset" portion can be a real
                                     drill-down link to the specific units missing a status. -->
                                <div class="hk-mix-idle" *ngIf="isHkMixIdle(loc)">
                                    <ng-container *ngIf="(loc.totalUnits || 0) === 0">—</ng-container>
                                    <ng-container *ngIf="(loc.totalUnits || 0) > 0">
                                        <ng-container *ngIf="(loc.physicalOccupied || 0) > 0">
                                            <span class="hk-mix-idle__part">{{ loc.physicalOccupied }} occupied</span>
                                        </ng-container>
                                        <ng-container *ngIf="(loc.physicalNotAvailable || 0) > 0">
                                            <span class="hk-mix-idle__sep"
                                                  *ngIf="(loc.physicalOccupied || 0) > 0"> · </span>
                                            <span class="hk-mix-idle__part">{{ loc.physicalNotAvailable }} NA</span>
                                        </ng-container>
                                        <ng-container *ngIf="hkUnsetCount(loc) > 0">
                                            <span class="hk-mix-idle__sep"
                                                  *ngIf="((loc.physicalOccupied || 0) + (loc.physicalNotAvailable || 0)) > 0"> · </span>
                                            <a class="hk-mix-idle__unset-link"
                                               href="javascript:void(0)"
                                               (click)="drilldownUnsetUnits(loc); $event.stopPropagation();"
                                               pTooltip="Units with NO occupancyStatus set (or an unknown status code). Most commonly the Breezeway sync never fired for these properties — either the integration isn't wired up for this location, the listing is owner-managed without a housekeeping workflow, or the property is brand-new and hasn't had a first task assigned. Click to see exactly which units."
                                               tooltipPosition="top">
                                                {{ hkUnsetCount(loc) }} unset
                                            </a>
                                        </ng-container>
                                    </ng-container>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <!-- Totals across every visible location.
                         locationTotals() returns null when data.locations is empty so the
                         tfoot won't render until the dashboard data lands. -->
                    <tfoot *ngIf="locationTotals() as totals">
                        <tr class="total-row">
                            <td>Total</td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(totals.totalUnits) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(totals.occupied) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtPct(totals.occupancyPct) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(totals.arrivals) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(totals.departures) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(totals.guests) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(totals.futonsOut) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(totals.rollawaysOut) }}</span></td>
                            <td>
                                <div class="hk-mix-bar" *ngIf="!isHkMixIdle(totals)">
                                    <div class="mix-seg mix-seg--clickable" [ngStyle]="mixBarStyle(totals, 'dirty')"
                                         (click)="drilldown('unitsDirty', 'Dirty Units', null); $event.stopPropagation();"
                                         [title]="'Dirty: ' + (totals.housekeepingMix?.dirty || 0) + ' — click to see units (all locations)'">{{ totals.housekeepingMix?.dirty || '' }}</div>
                                    <div class="mix-seg mix-seg--clickable" [ngStyle]="mixBarStyle(totals, 'housekeeping')"
                                         (click)="drilldown('unitsHousekeeping', 'Units in Housekeeping', null); $event.stopPropagation();"
                                         [title]="'Housekeeping: ' + (totals.housekeepingMix?.housekeeping || 0) + ' — click to see units (all locations)'">{{ totals.housekeepingMix?.housekeeping || '' }}</div>
                                    <div class="mix-seg mix-seg--clickable" [ngStyle]="mixBarStyle(totals, 'inspection')"
                                         (click)="drilldown('unitsInspection', 'Units in Inspection', null); $event.stopPropagation();"
                                         [title]="'Inspection: ' + (totals.housekeepingMix?.inspection || 0) + ' — click to see units (all locations)'">{{ totals.housekeepingMix?.inspection || '' }}</div>
                                    <div class="mix-seg mix-seg--clickable" [ngStyle]="mixBarStyle(totals, 'ready')"
                                         (click)="drilldown('vacantClean', 'Ready Units', null); $event.stopPropagation();"
                                         [title]="'Ready: ' + (totals.housekeepingMix?.ready || 0) + ' — click to see units (all locations)'">{{ totals.housekeepingMix?.ready || '' }}</div>
                                </div>
                                <div class="hk-mix-idle" *ngIf="isHkMixIdle(totals)">
                                    <ng-container *ngIf="(totals.totalUnits || 0) === 0">—</ng-container>
                                    <ng-container *ngIf="(totals.totalUnits || 0) > 0">
                                        <ng-container *ngIf="(totals.physicalOccupied || 0) > 0">
                                            <span class="hk-mix-idle__part">{{ totals.physicalOccupied }} occupied</span>
                                        </ng-container>
                                        <ng-container *ngIf="(totals.physicalNotAvailable || 0) > 0">
                                            <span class="hk-mix-idle__sep"
                                                  *ngIf="(totals.physicalOccupied || 0) > 0"> · </span>
                                            <span class="hk-mix-idle__part">{{ totals.physicalNotAvailable }} NA</span>
                                        </ng-container>
                                        <ng-container *ngIf="hkUnsetCount(totals) > 0">
                                            <span class="hk-mix-idle__sep"
                                                  *ngIf="((totals.physicalOccupied || 0) + (totals.physicalNotAvailable || 0)) > 0"> · </span>
                                            <a class="hk-mix-idle__unset-link"
                                               href="javascript:void(0)"
                                               (click)="drilldown('unitsUnsetStatus', 'Units with Unset Status', null); $event.stopPropagation();"
                                               pTooltip="Units with NO occupancyStatus set (or an unknown status code) across ALL locations. Click to see every property in this state — useful for spotting whole locations missing a Breezeway sync."
                                               tooltipPosition="top">
                                                {{ hkUnsetCount(totals) }} unset
                                            </a>
                                        </ng-container>
                                    </ng-container>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </section>

        <!-- ============================== Maintenance — Off Market Today ============================== -->
        <!-- Units with an NA-status reservation overlapping the selected end date.
             Backend: data.maintenance ({ total, byLocation: [{locationId, locationName, count}],
             units: [{ propertyCode, locationName, checkInDate, checkOutDate, reason, orderId, shortTermRentalId }] }).
             Hidden when there's nothing to show. -->
        <section class="ops-section" *ngIf="data.maintenance">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>
                        Maintenance — Off Market Today
                        <span class="kbma-badge" [ngClass]="(data.maintenance?.total || 0) > 0 ? 'kbma-badge--warning' : 'kbma-badge--info'">
                            <span class="numeric">{{ fmtNum(data.maintenance?.total) }}</span>
                            unit<ng-container *ngIf="(data.maintenance?.total || 0) !== 1">s</ng-container>
                        </span>
                    </h3>
                    <span class="caption">
                        Reservations with status <strong>NA</strong> (maintenance block) overlapping
                        <strong>{{ endDate | date:'MM/dd/yy' }}</strong> on active child units.
                    </span>
                </div>

                <!-- Per-location breakdown — only rows with at least one unit off-market.
                     Each chip is clickable: opens /reservations filtered to status=NA
                     for that location on endDate. -->
                <div class="maintenance-by-loc" *ngIf="data.maintenance?.byLocation?.length">
                    <span class="loc-chip"
                          *ngFor="let r of data.maintenance.byLocation"
                          [class.clickable]="r.count > 0"
                          (click)="r.count > 0 && openReservationsForMaintenance(r.locationId)"
                          [pTooltip]="r.count > 0 ? 'Click to see the ' + r.count + ' maintenance reservation(s) for ' + r.locationName + ' on the reservation list' : null"
                          tooltipPosition="top">
                        <ng-container *ngIf="r.count > 0">
                            <strong>{{ r.locationName }}</strong>:
                            <span class="numeric">{{ fmtNum(r.count) }}</span>
                        </ng-container>
                    </span>
                </div>

                <!-- Unit-level detail. Property → /property-edit/{strId}; Reservation reason
                     opens the order via orderUrl(). orderUrl returns a safe no-op when orderId
                     is null. -->
                <table class="kbma-table" *ngIf="data.maintenance?.units?.length">
                    <thead>
                        <tr>
                            <th>Property</th>
                            <th>Location</th>
                            <th>Start</th>
                            <th>End</th>
                            <th>Reason / Note</th>
                            <th>Order</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let u of data.maintenance.units">
                            <td>
                                <a *ngIf="u.shortTermRentalId != null; else maintPropPlain"
                                   class="property-link"
                                   [href]="propertyEditUrl(u.shortTermRentalId)"
                                   target="_blank" rel="noopener noreferrer">{{ u.propertyCode }}</a>
                                <ng-template #maintPropPlain>{{ u.propertyCode }}</ng-template>
                            </td>
                            <td>{{ u.locationName || '—' }}</td>
                            <td><span class="numeric">{{ u.checkInDate  | date:'MM/dd/yy' }}</span></td>
                            <td><span class="numeric">{{ u.checkOutDate | date:'MM/dd/yy' }}</span></td>
                            <td>{{ u.reason || '—' }}</td>
                            <td>
                                <a *ngIf="u.orderId != null; else maintOrderPlain"
                                   class="property-link"
                                   [href]="orderUrl(u.orderId)"
                                   target="_blank" rel="noopener noreferrer">#{{ u.orderId }}</a>
                                <ng-template #maintOrderPlain>—</ng-template>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div *ngIf="!data.maintenance?.units?.length" class="empty">
                    No units off the market today. ✓
                </div>
            </div>
        </section>

        <!-- ============================== Staffing Recommendation ============================== -->
        <section class="ops-section staffing-row" *ngIf="data.staffing && data.staffing.byLocation?.length">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>
                        Staffing &amp; Supply Recommendation
                        <i class="pi pi-info-circle th-info"
                           pTooltip="FTE = Full-Time Equivalent. 1.0 FTE ≈ one full-day shift (one worker working a normal day). 2.0 FTE = two workers (or one worker for two days). Recommended FTE is a workload-derived TARGET, not a commitment — compare it to your actual roster to detect over- or under-staffing. Fractional values are rounded UP (ceil) so a 1.2 FTE workload triggers a 2-person plan rather than gambling on one person catching up."
                           tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                    </h3>
                    <span class="caption">
                        <strong>FTE = Full-Time Equivalent</strong> · 1.0 = one full-day shift.
                        {{ data.staffing?.ratios?.note }}
                    </span>
                </div>

                <!-- Totals strip — each role tile drills to its underlying unit/reservation list -->
                <div class="staffing-totals" *ngIf="data.staffing.totals">
                    <div class="totals-cell">
                        <div class="kbma-kpi__label">Total Recommended</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.staffing.totals.staffing?.totalRecommendedFte) }}</span> FTE</div>
                    </div>
                    <div class="totals-cell clickable"
                         *ngFor="let r of data.staffing.totals.staffing?.roles"
                         (click)="drilldown(staffingDrilldownMetric(r.role), r.role + ' — underlying load')"
                         [title]="'Click to see the units driving the ' + r.role + ' recommendation'">
                        <div class="kbma-kpi__label">{{ r.role }} <span class="drilldown-arrow">↗</span></div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(r.recommendedFte) }}</span></div>
                        <div class="basis-line">{{ r.basis }}</div>
                    </div>
                </div>

                <!-- Per-location detail -->
                <table class="kbma-table staffing-table">
                    <thead>
                        <tr>
                            <th>
                                Location
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="KBM's 6 location groups: West Maui · South Maui · Upcountry Maui · Big Island - Kohala · Oahu · Park City."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Arrivals
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Reservations checking in within the selected range (same canonical filter as the per-location Operations table — excludes composite listings and Hold reservations)."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Departures
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Reservations checking out within the selected range."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                B2B
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Back-to-back turnovers — units that both check out AND check in on the same calendar day. Two cleans compressed into one short window; counted with a +25% urgency weight in the Cleans Today formula. (Same field that was previously labelled 'Same-Day TO'.)"
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Cleans Today
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Departures + ⌈Same-Day TO × 0.25⌉. Each departure generates a clean; each same-day turnover counts once with a 25% urgency uplift. Drives the housekeeper FTE recommendation."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Housekeeper
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Recommended housekeeper FTE for the day = ⌈Cleans Today ÷ HK_UNITS_PER_FTE_PER_DAY⌉. The constant lives in OperationsDashboardBL and is a rule-of-thumb pending DOGE calibration."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Inspector
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Recommended inspector FTE = ⌈(Cleans Today + units in inspection states) ÷ INSPECTOR_UNITS_PER_FTE⌉. Inspection states = READY_FOR_INSPECTION + INSPECTION."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Guest Svc
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Recommended guest-services FTE = ⌈Arrivals ÷ GUEST_SERVICES_PER_ARRIVALS⌉."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Maint.
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Recommended maintenance FTE = ⌈physically-occupied units ÷ MAINTENANCE_PER_OCCUPIED⌉. NOTE: this uses the Breezeway RoomStatusEnum count (OCCUPIED + GUEST_CHECKED_IN), not the canonical OP/NA/OO/OG set — staffing tracks who is physically in a unit, not who has a booking."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Linens
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Linen carts needed for today's arrivals = LINENS_PER_ARRIVAL × Arrivals."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <th class="numeric">
                                Towels
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Towel sets needed for today's arrivals = TOWELS_PER_ARRIVAL × Arrivals."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                            </th>
                            <!-- Amenities column removed per manager request — backend
                                 still computes the qty (supply.items[2]) in case a future
                                 view wants it, but it's no longer displayed here. -->
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let loc of data.staffing.byLocation">
                            <td>{{ loc.locationName }}</td>
                            <td class="numeric cell-clickable" (click)="cellDrill('arrivals',     loc.locationId)"><span class="numeric">{{ fmtNum(loc.workload?.arrivals) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('departures',   loc.locationId)"><span class="numeric">{{ fmtNum(loc.workload?.departures) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('sameDayTo',    loc.locationId)"><span class="numeric">{{ fmtNum(loc.workload?.sameDayTurnovers) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('cleansToday',  loc.locationId)"><span class="numeric">{{ fmtNum(loc.workload?.cleansToday) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('housekeeper',  loc.locationId)"><span class="numeric">{{ fmtNum(loc.staffing?.roles?.[0]?.recommendedFte) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('inspector',    loc.locationId)"><span class="numeric">{{ fmtNum(loc.staffing?.roles?.[1]?.recommendedFte) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('guestSvc',     loc.locationId)"><span class="numeric">{{ fmtNum(loc.staffing?.roles?.[2]?.recommendedFte) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('maint',        loc.locationId)"><span class="numeric">{{ fmtNum(loc.staffing?.roles?.[3]?.recommendedFte) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('linens',       loc.locationId)"><span class="numeric">{{ fmtNum(loc.supply?.items?.[0]?.qty) }}</span></td>
                            <td class="numeric cell-clickable" (click)="cellDrill('towels',       loc.locationId)"><span class="numeric">{{ fmtNum(loc.supply?.items?.[1]?.qty) }}</span></td>
                        </tr>
                    </tbody>
                    <!-- Totals row. Reads directly from data.staffing.totals (built
                         by the backend via addToTotals + totalsBlock) so the workload
                         sums and the role-FTE recomputation stay authoritative on the
                         server side. Hidden until totals lands. -->
                    <tfoot *ngIf="data.staffing?.totals as t">
                        <tr class="total-row">
                            <td>Total</td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.workload?.arrivals) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.workload?.departures) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.workload?.sameDayTurnovers) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.workload?.cleansToday) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.staffing?.roles?.[0]?.recommendedFte) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.staffing?.roles?.[1]?.recommendedFte) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.staffing?.roles?.[2]?.recommendedFte) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.staffing?.roles?.[3]?.recommendedFte) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.supply?.items?.[0]?.qty) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(t.supply?.items?.[1]?.qty) }}</span></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </section>

        <!-- ============================== §13 Housekeeping Linen Report ============================== -->
        <section class="ops-section linen-row" *ngIf="data.housekeepingLinen">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>Housekeeping — Linen Required</h3>
                    <span class="caption">{{ data.housekeepingLinen?.ratios?.note }}</span>
                </div>

                <!-- Totals by linen size (clickable to filter the table below) -->
                <div class="linen-totals">
                    <div class="linen-size-card clickable"
                         *ngFor="let size of linenSizeOrder"
                         [class.linen-size-card--active]="selectedLinenSize === size"
                         (click)="selectLinenSize(size)"
                         [title]="'Click to show only units that need ' + size + ' linen'">
                        <div class="linen-size-card__header">{{ size }}</div>
                        <div class="linen-size-card__stats">
                            <div>
                                <div class="kbma-kpi__label">Pillows</div>
                                <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.housekeepingLinen?.totals?.[size]?.pillows) }}</span></div>
                            </div>
                            <div>
                                <div class="kbma-kpi__label">Sheets</div>
                                <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.housekeepingLinen?.totals?.[size]?.sheets) }}</span></div>
                            </div>
                        </div>
                    </div>
                    <div class="linen-size-card grand-total clickable"
                         [class.linen-size-card--active]="selectedLinenSize === 'Total' || selectedLinenSize === null"
                         (click)="selectLinenSize('Total')"
                         title="Show all units">
                        <div class="linen-size-card__header">Total</div>
                        <div class="linen-size-card__stats">
                            <div>
                                <div class="kbma-kpi__label">Pillows</div>
                                <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.housekeepingLinen?.totals?.totalPillows) }}</span></div>
                            </div>
                            <div>
                                <div class="kbma-kpi__label">Sheets</div>
                                <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.housekeepingLinen?.totals?.totalSheets) }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Towel totals strip — same visual format as the pillow/sheet
                     cards above, but sourced from the bathroom-roster-driven
                     bath/hand/face counts (totalBathTowels / totalHandTowels /
                     totalFaceTowels in the backend totals payload). -->
                <div class="linen-totals linen-totals--towels">
                    <div class="linen-size-card">
                        <div class="linen-size-card__header">Bath Towels</div>
                        <div class="linen-size-card__stats">
                            <div>
                                <div class="kbma-kpi__label">Total</div>
                                <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.housekeepingLinen?.totals?.totalBathTowels) }}</span></div>
                            </div>
                            <div class="linen-size-card__formula">3 per Full / 3/4 bath</div>
                        </div>
                    </div>
                    <div class="linen-size-card">
                        <div class="linen-size-card__header">Hand Towels</div>
                        <div class="linen-size-card__stats">
                            <div>
                                <div class="kbma-kpi__label">Total</div>
                                <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.housekeepingLinen?.totals?.totalHandTowels) }}</span></div>
                            </div>
                            <div class="linen-size-card__formula">3 per Full / 3/4 · 2 per Half</div>
                        </div>
                    </div>
                    <div class="linen-size-card">
                        <div class="linen-size-card__header">Face Towels</div>
                        <div class="linen-size-card__stats">
                            <div>
                                <div class="kbma-kpi__label">Total</div>
                                <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.housekeepingLinen?.totals?.totalFaceTowels) }}</span></div>
                            </div>
                            <div class="linen-size-card__formula">3 per Full / 3/4 · 1 per Half</div>
                        </div>
                    </div>
                </div>

                <!-- Per-unit detail table (filtered by tile selection) -->
                <div class="linen-detail-header">
                    <span>
                        Per-unit detail
                        <span *ngIf="selectedLinenSize && selectedLinenSize !== 'Total'" class="filter-chip">
                            Filter: {{ selectedLinenSize }}
                            <button class="filter-chip__close" (click)="selectLinenSize(null)" title="Clear filter">×</button>
                        </span>
                    </span>
                    <span class="caption">
                        Showing <span class="numeric">{{ fmtNum(linenUnits().length) }}</span>
                        of <span class="numeric">{{ fmtNum(data.housekeepingLinen?.unitsCounted) }}</span>
                        units with bed roster ·
                        <span class="numeric">{{ fmtNum(data.housekeepingLinen?.unitsTotal) }}</span>
                        total arrivals in range
                    </span>
                </div>
                <!-- Per-unit detail. All column headers are sortable in-memory
                     (no server round-trip): click a header to sort ascending,
                     click again to flip direction. Active column shows a
                     ▲ / ▼ indicator. The sort state lives on the dashboard
                     component (linenSortField + linenSortDir) and is preserved
                     across linen-size filter changes. -->
                <table class="kbma-table linen-table" *ngIf="linenUnits().length">
                    <thead>
                        <!-- Column-group header row — visually groups the
                             reservation columns (6 leading), the towel
                             columns (3), and the pillow/sheet-by-size
                             columns (4) so the manager can scan at a glance.
                             Dividers via .linen-group-div border-left. -->
                        <tr class="linen-group-row">
                            <th colspan="6" class="linen-group-th"></th>
                            <th colspan="3" class="linen-group-th linen-group-div linen-group-towels">TOWELS</th>
                            <th colspan="4" class="linen-group-th linen-group-div linen-group-pillows">PILLOWS / SHEETS</th>
                            <th colspan="2" class="linen-group-th linen-group-div linen-group-totals">TOTAL</th>
                        </tr>
                        <tr>
                            <th class="sortable" (click)="sortLinenBy('property')">
                                Property
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Short-term-rental property code for the arriving unit. Click to open the property edit page."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('property') }}</span>
                            </th>
                            <th class="sortable" (click)="sortLinenBy('guest')">
                                Guest
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Guest LAST NAME from the reservation profile (compact format). Click to open the reservation's order page in a new tab. Sort key uses the last name only."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('guest') }}</span>
                            </th>
                            <th class="sortable" (click)="sortLinenBy('checkInDate')">
                                Check-in
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Check-in calendar date for this arrival."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('checkInDate') }}</span>
                            </th>
                            <th class="sortable" (click)="sortLinenBy('bedrooms')">
                                Unit Type
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Bedroom count for the unit (ShortTermRentalVO.numBedrooms). Studio shows as '—'."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('bedrooms') }}</span>
                            </th>
                            <th class="sortable" (click)="sortLinenBy('beds')">
                                Beds
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Compact bed-roster string per unit. Format: '{count}{letter}' for each bed type, space-separated. Letter key — K = King, Q = Queen, F = Full (double), T = Twin. Example: '2K 1Q 4T' = 2 Kings + 1 Queen + 4 Twins. Sorted by total bed count when this header is the active sort key."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('beds') }}</span>
                            </th>
                            <!-- Sofabed values are single digits (0–2 in practice) so the default
                                 right-aligned numeric style makes the value float far from the
                                 header. Override with sofabed-cell class to left-align both the
                                 header label and the value. -->
                            <th class="sortable sofabed-cell" (click)="sortLinenBy('sofabed')">
                                Sofabed
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Quantity of sofabed / couchbed / sleeper line items ordered for this reservation (from str_order_detail). Independent of futon / rollaway."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('sofabed') }}</span>
                            </th>
                            <th class="numeric sortable linen-group-div" (click)="sortLinenBy('bathTowels')">
                                Bath
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Bath towels for the unit, computed from the bathroom roster (str_bathroom.type) × TOWELS_PER_BATHROOM_TYPE. Full + 3/4 baths (FU/TQ/TT) get 3 each; Half baths (HA) get 0."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('bathTowels') }}</span>
                            </th>
                            <th class="numeric sortable" (click)="sortLinenBy('handTowels')">
                                Hand
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Hand towels for the unit. Full + 3/4 baths (FU/TQ/TT) get 3 each; Half baths (HA) get 2."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('handTowels') }}</span>
                            </th>
                            <th class="numeric sortable" (click)="sortLinenBy('faceTowels')">
                                Face
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Face towels (washcloths) for the unit. Full + 3/4 baths (FU/TQ/TT) get 3 each; Half baths (HA) get 1."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('faceTowels') }}</span>
                            </th>
                            <th class="numeric sortable linen-group-div" [class.linen-col--highlight]="selectedLinenSize === 'King'" (click)="sortLinenBy('king')">
                                King P/S
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="King-bed Pillows / Sheets needed at this unit — 2 pillows + 2 sheets per king bed."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('king') }}</span>
                            </th>
                            <th class="numeric sortable" [class.linen-col--highlight]="selectedLinenSize === 'Queen'" (click)="sortLinenBy('queen')">
                                Queen P/S
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Queen-bed Pillows / Sheets needed — 2 pillows + 2 sheets per queen bed."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('queen') }}</span>
                            </th>
                            <th class="numeric sortable" [class.linen-col--highlight]="selectedLinenSize === 'Full'" (click)="sortLinenBy('full')">
                                Full P/S
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Full / Double-bed Pillows / Sheets needed — 2 pillows + 2 sheets per full bed."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('full') }}</span>
                            </th>
                            <th class="numeric sortable" [class.linen-col--highlight]="selectedLinenSize === 'Twin'" (click)="sortLinenBy('twin')">
                                Twin P/S
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Twin-bed Pillows / Sheets needed — 1 pillow + 1 sheet per twin bed."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('twin') }}</span>
                            </th>
                            <th class="numeric sortable linen-group-div" (click)="sortLinenBy('totalPillows')">
                                Total Pillows
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Sum of pillows across all bed sizes for this unit. Drives the per-arrival linen-cart load."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('totalPillows') }}</span>
                            </th>
                            <th class="numeric sortable" (click)="sortLinenBy('totalSheets')">
                                Total Sheets
                                <i class="pi pi-info-circle th-info"
                                   pTooltip="Sum of sheets across all bed sizes for this unit."
                                   tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                <span class="sort-ind">{{ linenSortIcon('totalSheets') }}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let u of linenUnits()">
                            <td>
                                <a *ngIf="u.shortTermRentalId != null; else linenPropPlain"
                                   class="property-link"
                                   [href]="propertyEditUrl(u.shortTermRentalId)"
                                   target="_blank"
                                   rel="noopener noreferrer">{{ u.propertyCode }}</a>
                                <ng-template #linenPropPlain>{{ u.propertyCode }}</ng-template>
                            </td>
                            <!-- Guest cell: show LAST NAME only (manager request — keeps the
                                 column compact). Link points to /order/{orderId} so a click
                                 jumps to the reservation's order page. Falls back to plain
                                 text when orderId isn't on the row (rare; mostly for
                                 in-flight reservations not yet attached to an order). -->
                            <td>
                                <a *ngIf="u.orderId != null; else guestPlain"
                                   class="property-link"
                                   [href]="orderUrl(u.orderId)"
                                   target="_blank"
                                   rel="noopener noreferrer">{{ u.guestLastName || '—' }}</a>
                                <ng-template #guestPlain>{{ u.guestLastName || '—' }}</ng-template>
                            </td>
                            <td><span class="numeric">{{ u.checkInDate | date:'MM/dd' }}</span></td>
                            <!-- Unit Type: "{bedrooms} BR / {bathrooms} BA" — bathroom
                                 count comes from the SAME str_bathroom roster that
                                 drives the bath/hand/face towel columns, so the math
                                 is internally consistent (e.g. 3 BR / 2 BA → 6 bath
                                 towels via 3-per-bath × 2 baths). -->
                            <td>
                                <ng-container *ngIf="u.bedrooms != null; else unitTypeEmpty">
                                    <span class="numeric">{{ u.bedrooms }}</span> BR
                                    <ng-container *ngIf="(u.totalBathrooms || 0) > 0">
                                        / <span class="numeric">{{ u.totalBathrooms }}</span> BA
                                    </ng-container>
                                </ng-container>
                                <ng-template #unitTypeEmpty>—</ng-template>
                            </td>
                            <td>
                                <span *ngFor="let s of linenSizeOrder">
                                    <span *ngIf="u.bedCounts?.[s]">
                                        <span class="numeric">{{ u.bedCounts[s] }}</span>{{ s.charAt(0) }}&nbsp;
                                    </span>
                                </span>
                            </td>
                            <!-- Left-aligned sofabed cell — matches the .sofabed-cell header. -->
                            <td class="sofabed-cell"><span class="numeric">{{ fmtNum(u.sofabedCount) }}</span></td>
                            <td class="numeric linen-group-div"><span class="numeric">{{ fmtNum(u.bathTowels) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(u.handTowels) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(u.faceTowels) }}</span></td>
                            <td class="numeric linen-group-div" [class.linen-col--highlight]="selectedLinenSize === 'King'"><span class="numeric">{{ fmtNum(u.pillowsBySize?.King) }}</span>/<span class="numeric">{{ fmtNum(u.sheetsBySize?.King) }}</span></td>
                            <td class="numeric" [class.linen-col--highlight]="selectedLinenSize === 'Queen'"><span class="numeric">{{ fmtNum(u.pillowsBySize?.Queen) }}</span>/<span class="numeric">{{ fmtNum(u.sheetsBySize?.Queen) }}</span></td>
                            <td class="numeric" [class.linen-col--highlight]="selectedLinenSize === 'Full'"><span class="numeric">{{ fmtNum(u.pillowsBySize?.Full) }}</span>/<span class="numeric">{{ fmtNum(u.sheetsBySize?.Full) }}</span></td>
                            <td class="numeric" [class.linen-col--highlight]="selectedLinenSize === 'Twin'"><span class="numeric">{{ fmtNum(u.pillowsBySize?.Twin) }}</span>/<span class="numeric">{{ fmtNum(u.sheetsBySize?.Twin) }}</span></td>
                            <td class="numeric linen-group-div"><span class="numeric">{{ fmtNum(u.totalPillows) }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(u.totalSheets) }}</span></td>
                        </tr>
                    </tbody>
                </table>
                <div *ngIf="!linenUnits().length" class="empty">
                    <ng-container *ngIf="selectedLinenSize && selectedLinenSize !== 'Total'; else noUnits">
                        No units in range need <strong>{{ selectedLinenSize }}</strong> linen.
                    </ng-container>
                    <ng-template #noUnits>No arrivals with bed data in range.</ng-template>
                </div>
            </div>
        </section>

        <!-- ============================== §2 Equipment-Pool Performance ============================== -->
        <section class="ops-section equipment-row">

            <div class="ops-card" [ngClass]="poolUtilClass(data.equipment?.futon)">
                <div class="ops-card__header">
                    <h3>Futons</h3>
                    <span class="kbma-badge kbma-badge--info">{{ fmtPct(data.equipment?.futon?.utilization) }} used</span>
                    <i class="pi pi-info-circle ops-info"
                       pTooltip="Futon line items in str_order_detail (OrderProductEnum.FUTON, id=342 — canonical ID used by FutonAvailabilityBL). 'Out' = futons whose reservation overlaps today. 'Revenue 30d' = sum of line totals for futon stays overlapping the last 30 days (same stay-window semantics as Out — so the two figures stay consistent). Active reservation statuses only (OP/OC/OG/OO)."
                       tooltipPosition="top"></i>
                </div>
                <div class="equipment-grid">
                    <div class="donut-wrap"><canvas #futonDonut></canvas></div>
                    <div class="equipment-stats">
                        <!-- Pool + Available stat rows removed per manager request — the
                             headline metrics on this card are Out today and 30d revenue.
                             Utilization badge in the card header still surfaces "% in use". -->
                        <div class="stat-row clickable"
                             (click)="drilldown('futonsOut', 'Futons Out')"
                             pTooltip="Click to see the futon line items whose reservation overlaps today — one row per line, with property, guest, check-in / check-out, status."
                             tooltipPosition="left">
                            <span class="label">Out</span><span class="numeric">{{ fmtNum(data.equipment?.futon?.totalOut) }}</span>
                        </div>
                        <div class="stat-row clickable"
                             (click)="drilldown('futonsRevenue', 'Futon Revenue Line Items (30d)')"
                             pTooltip="Click to see futon line items whose stay overlapped the last 30 days — one row per line, with property, guest, check-in / check-out, list price, line total."
                             tooltipPosition="left">
                            <span class="label">Revenue 30d</span><span class="numeric">{{ fmtCurrency(data.equipment?.futon?.revenue30d) }}</span>
                        </div>
                    </div>
                </div>
                <div class="spark-wrap"><canvas #futonSpark></canvas></div>
                <div class="top-props" *ngIf="data.equipment?.futon?.topProperties?.length">
                    <div class="label">Top demand today</div>
                    <ul>
                        <li *ngFor="let p of data.equipment.futon.topProperties">
                            <span>{{ p.propertyCode }}</span> <span class="numeric">{{ fmtNum(p.quantity) }}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="ops-card" [ngClass]="poolUtilClass(data.equipment?.rollaway)">
                <div class="ops-card__header">
                    <h3>Rollaways</h3>
                    <span class="kbma-badge kbma-badge--info">{{ fmtPct(data.equipment?.rollaway?.utilization) }} used</span>
                    <i class="pi pi-info-circle ops-info"
                       pTooltip="Rollaway line items in str_order_detail (OrderProductEnum.ROLL_AWAY_BED, id=26 — canonical ID used by WikiGuestReservationBL). 'Out' = rollaways whose reservation overlaps today. 'Revenue 30d' = sum of line totals for rollaway stays overlapping the last 30 days (same stay-window semantics as Out — so the two figures stay consistent). Active reservation statuses only (OP/OC/OG/OO)."
                       tooltipPosition="top"></i>
                </div>
                <div class="equipment-grid">
                    <div class="donut-wrap"><canvas #rollawayDonut></canvas></div>
                    <div class="equipment-stats">
                        <!-- Pool + Available stat rows removed per manager request. -->
                        <div class="stat-row clickable"
                             (click)="drilldown('rollawaysOut', 'Rollaways Out')"
                             pTooltip="Click to see the rollaway line items whose reservation overlaps today — one row per line, with property, guest, check-in / check-out, status."
                             tooltipPosition="left">
                            <span class="label">Out</span><span class="numeric">{{ fmtNum(data.equipment?.rollaway?.totalOut) }}</span>
                        </div>
                        <div class="stat-row clickable"
                             (click)="drilldown('rollawaysRevenue', 'Rollaway Revenue Line Items (30d)')"
                             pTooltip="Click to see rollaway line items whose stay overlapped the last 30 days — one row per line, with property, guest, check-in / check-out, list price, line total."
                             tooltipPosition="left">
                            <span class="label">Revenue 30d</span><span class="numeric">{{ fmtCurrency(data.equipment?.rollaway?.revenue30d) }}</span>
                        </div>
                    </div>
                </div>
                <div class="spark-wrap"><canvas #rollawaySpark></canvas></div>
                <div class="top-props" *ngIf="data.equipment?.rollaway?.topProperties?.length">
                    <div class="label">Top demand today</div>
                    <ul>
                        <li *ngFor="let p of data.equipment.rollaway.topProperties">
                            <span>{{ p.propertyCode }}</span> <span class="numeric">{{ fmtNum(p.quantity) }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </section>

        <!-- ============================== §4 Housekeeping Efficiency ============================== -->
        <section class="ops-section housekeeping-row">
            <div class="ops-card">
                <div class="ops-card__header"><h3>Housekeeping Efficiency</h3></div>
                <div class="hk-stats">
                    <!-- Cleans Today — projected workload (departures + ceil(B2B × 0.25)).
                         Reads straight from data.staffing.totals.workload.cleansToday so
                         this tile is GUARANTEED to equal the "Cleans Today" total in the
                         Staffing & Supply Recommendation table above. The old "Cleaned
                         Today" tile (historical JaVers transition count) was removed —
                         the actual completion count is still in the API payload at
                         data.housekeeping.roomsCleanedToday if anyone needs it. -->
                    <div class="hk-stat">
                        <div class="kbma-kpi__label">
                            Cleans Today
                            <i class="pi pi-info-circle th-info"
                               pTooltip="Projected number of cleans needed today. Formula: Departures + ⌈Same-day turnovers × 0.25⌉. SAME value (and same definition) as the 'Cleans Today' column total in the Staffing & Supply Recommendation table above — the two tiles always match because this one reads data.staffing.totals.workload.cleansToday directly."
                               tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                        </div>
                        <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.staffing?.totals?.workload?.cleansToday) }}</span></div>
                    </div>
                    <!-- Cleaning Backlog. Both this tile and the Staffing payload compute it
                         from the SAME status set (DIRTY / HOUSEKEEPING / READY_FOR_INSPECTION /
                         INSPECTION), so the two sections agree by construction. -->
                    <div class="hk-stat clickable" (click)="drilldown('cleaningBacklog')">
                        <div class="kbma-kpi__label">
                            Cleaning Backlog
                            <i class="pi pi-info-circle th-info"
                               pTooltip="Count of active child units currently in any cleaning-related RoomStatusEnum: DIRTY · HOUSEKEEPING · READY_FOR_INSPECTION · INSPECTION. Identical formula to the per-location cleaningBacklog field used by the Staffing & Supply Recommendation table — the totals match by construction. Click the tile to drill into the underlying unit list."
                               tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                        </div>
                        <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.housekeeping?.cleaningBacklog) }}</span></div>
                    </div>
                    <div class="hk-stat">
                        <div class="kbma-kpi__label">
                            Avg Turnaround (h)
                            <i class="pi pi-info-circle th-info"
                               pTooltip="Mean DIRTY → CLEAN_READY elapsed time over the LAST 7 DAYS (ending on the dashboard's selected end date). Computed from the JaVers occupancyStatus change history: for each unit, every consecutive DIRTY-then-CLEAN_READY commit pair contributes one duration. Sum of durations ÷ number of pairs, converted from minutes to hours and rounded to 1 decimal. Returns 0 when no pairs exist in the window."
                               tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                        </div>
                        <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.housekeeping?.avgTurnaroundHours) }}</span></div>
                    </div>
                </div>
            </div>

            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>
                        Priority Rooms — Same-Day Arrivals (B2B)
                        <i class="pi pi-info-circle th-info"
                           pTooltip="Units with a SAME-DAY TURNOVER (B2B = Back-to-Back): a guest checks out today AND a different guest checks in today on the same unit. Row count here ALWAYS equals the Same-day TO / B2B total in the Staffing & Supply Recommendation table — same predicate, same scope (countSameDayTurnovers). All KBM check-ins are at 4:00 PM by policy, so housekeeping must have these units in CLEAN_READY state by 4 PM. Status cell shows the unit's current RoomStatusEnum so the team can see at a glance which are still mid-clean."
                           tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                    </h3>
                    <span class="caption">
                        All KBM check-ins are at <strong>4:00 PM</strong>. Units below have a guest
                        leaving today AND a new guest arriving today — must be CLEAN_READY by 4 PM.
                    </span>
                </div>
                <!-- Scroll wrapper — caps visible height at ~8 rows so busy days
                     (15+ B2Bs) don't push the rest of the dashboard down the page.
                     thead stays pinned via the kbma-table sticky-thead convention. -->
                <div class="priority-scroll" *ngIf="data.housekeeping?.priorityRooms?.length">
                    <table class="kbma-table priority-table">
                        <thead>
                            <tr><th>Property</th><th>Status</th><th>Check-in</th></tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let r of data.housekeeping.priorityRooms">
                                <td>
                                    <a *ngIf="r.shortTermRentalId != null; else priorityPropPlain"
                                       class="property-link"
                                       [href]="propertyEditUrl(r.shortTermRentalId)"
                                       target="_blank" rel="noopener noreferrer">{{ r.propertyCode }}</a>
                                    <ng-template #priorityPropPlain>{{ r.propertyCode }}</ng-template>
                                </td>
                                <!-- Status as a coloured pill. White text + shadow keeps the
                                     label readable on every RoomStatusEnum colour (mix-bar
                                     palette). Same colour convention as the housekeeping mix bar. -->
                                <td>
                                    <span class="status-pill"
                                          [style.background-color]="roomStatusColor(r.occupancyStatus)">
                                        {{ roomStatusName(r.occupancyStatus) }}
                                    </span>
                                </td>
                                <!-- Hard-coded 4:00 PM — the actual checkInDateTime is stored
                                     date-only at midnight, so formatting HH:mm produced an
                                     unhelpful "00:00". KBM policy: every guest checks in at 4 PM. -->
                                <td><span class="numeric">4:00 PM</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div *ngIf="!data.housekeeping?.priorityRooms?.length" class="empty">
                    No same-day turnovers — nothing flipping over today.
                </div>
            </div>
        </section>

        <!-- ============================== §4.5 Amenities & Consumables ============================== -->
        <!-- Per-arrival amenity / consumable kit driven by the unit's bathroom
             roster (str_bathroom.type) and bedroom count:
                coffee + welcome gift    = 1 per arriving UNIT
                shampoo / conditioner /
                body wash / hand soap    = 1 per Full or 3/4 bathroom (FU / TQ / TT)
                hand soap (extra)        = 1 per Half bath (HA) — hand soap only
             Backend: data.amenities { totals, rules, byUnit }. Hidden when there
             are no arrivals (data.amenities?.byUnit is empty). -->
        <section class="ops-section amenities-section" *ngIf="data.amenities">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>
                        Amenities &amp; Consumables
                        <i class="pi pi-info-circle th-info"
                           pTooltip="Per-arrival housekeeping kit assembled before each guest check-in. Coffee + welcome gift = 1 per unit. Shampoo / conditioner / body wash = 1 per Full or 3/4 bath (FU / TQ / TT) — half baths get none since no shower / tub. Hand soap = 1 kitchen default per unit + 1 per bathroom of ANY type (FU / TQ / TT / HA) — every unit has a kitchen sink that isn't represented in str_bathroom, so it's modeled as a per-unit constant on top of the bathroom roster."
                           tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                    </h3>
                    <span class="caption">
                        Coffee + welcome gift: <strong>1 per unit</strong>.
                        Shampoo / conditioner / body wash: <strong>1 per Full or 3/4 bath</strong> (FU / TQ / TT).
                        Hand soap: <strong>1 kitchen + 1 per bathroom</strong> (any type).
                    </span>
                </div>

                <!-- Aggregate totals strip. Each tile = one kit item across all today's arrivals. -->
                <div class="staffing-totals" *ngIf="data.amenities?.totals">
                    <div class="totals-cell">
                        <div class="kbma-kpi__label">Arriving Units</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.amenities.totals.units) }}</span></div>
                    </div>
                    <div class="totals-cell">
                        <div class="kbma-kpi__label">Coffee</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.amenities.totals.coffee) }}</span></div>
                        <div class="basis-line">1 per unit</div>
                    </div>
                    <div class="totals-cell">
                        <div class="kbma-kpi__label">Welcome Gift</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.amenities.totals.welcomeGift) }}</span></div>
                        <div class="basis-line">1 per unit</div>
                    </div>
                    <div class="totals-cell">
                        <div class="kbma-kpi__label">Shampoo</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.amenities.totals.shampoo) }}</span></div>
                        <div class="basis-line">1 per FU / TQ / TT bath</div>
                    </div>
                    <div class="totals-cell">
                        <div class="kbma-kpi__label">Conditioner</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.amenities.totals.conditioner) }}</span></div>
                        <div class="basis-line">1 per FU / TQ / TT bath</div>
                    </div>
                    <div class="totals-cell">
                        <div class="kbma-kpi__label">Body Wash</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.amenities.totals.bodyWash) }}</span></div>
                        <div class="basis-line">1 per FU / TQ / TT bath</div>
                    </div>
                    <div class="totals-cell">
                        <div class="kbma-kpi__label">Hand Soap</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.amenities.totals.handSoap) }}</span></div>
                        <div class="basis-line">1 kitchen + 1 per bathroom (any type)</div>
                    </div>
                </div>

                <!-- Per-unit detail table — Property → /property-edit/{strId},
                     Guest → /order/{orderId}. Wrapped in .amenities-scroll so a
                     full arrivals day (20+ rows) doesn't push surrounding sections
                     down the page; tbody scrolls inside the wrapper with the
                     thead and tfoot pinned. -->
                <div class="amenities-scroll" *ngIf="data.amenities?.byUnit?.length">
                    <table class="kbma-table">
                        <thead>
                            <tr>
                                <th>Property</th>
                                <th>Guest</th>
                                <th>Check-in</th>
                                <th class="numeric">BR</th>
                                <th class="numeric">Full/3-Q Baths</th>
                                <th class="numeric">Half Baths</th>
                                <th class="numeric">Coffee</th>
                                <th class="numeric">Welcome</th>
                                <th class="numeric">Shampoo</th>
                                <th class="numeric">Conditioner</th>
                                <th class="numeric">Body Wash</th>
                                <th class="numeric">Hand Soap</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let u of data.amenities.byUnit">
                                <td>
                                    <a *ngIf="u.shortTermRentalId != null; else amenPropPlain"
                                       class="property-link"
                                       [href]="propertyEditUrl(u.shortTermRentalId)"
                                       target="_blank" rel="noopener noreferrer">{{ u.propertyCode }}</a>
                                    <ng-template #amenPropPlain>{{ u.propertyCode }}</ng-template>
                                </td>
                                <td>
                                    <a *ngIf="u.orderId != null; else amenGuestPlain"
                                       class="property-link"
                                       [href]="orderUrl(u.orderId)"
                                       target="_blank" rel="noopener noreferrer">{{ guestName(u) }}</a>
                                    <ng-template #amenGuestPlain>{{ guestName(u) }}</ng-template>
                                </td>
                                <td><span class="numeric">{{ u.checkInDate | date:'MM/dd' }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.bedrooms) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.fullishBaths) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.halfBaths) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.coffee) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.welcomeGift) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.shampoo) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.conditioner) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.bodyWash) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(u.handSoap) }}</span></td>
                            </tr>
                        </tbody>
                        <!-- Totals row pulled straight from data.amenities.totals. -->
                        <tfoot *ngIf="data.amenities?.totals as t">
                            <tr class="total-row">
                                <td>Total</td>
                                <td>—</td>
                                <td>—</td>
                                <td class="numeric">—</td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(t.fullishBaths) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(t.halfBaths) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(t.coffee) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(t.welcomeGift) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(t.shampoo) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(t.conditioner) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(t.bodyWash) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(t.handSoap) }}</span></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div *ngIf="!data.amenities?.byUnit?.length" class="empty">
                    No arrivals in range — no amenity kits to assemble.
                </div>
            </div>
        </section>

        <!-- §5 Data Health section removed per manager request.
             Backend still emits data.dataHealth in the API payload
             (Breezeway sync · VRBO LOS / avail push · orphan integrity
             counts · OTA channel status table). To reinstate: restore the
             previous <section class="ops-section"> block from git history
             (no backend change required). -->

        <!-- ============================== §6 Future Occupancy Heat Strip ============================== -->
        <section class="ops-section">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>
                        Future Occupancy — Next 30 Days
                        <i class="pi pi-info-circle th-info"
                           pTooltip="Per-location forecasted occupancy for each of the next 30 days. Each cell = booked unit-nights for that location ÷ active unit count on that day, bucketed into low / medium / high. The number inside each cell is the integer percentage (e.g. 73 = 73 %). Cell colour encodes the bucket — see the legend on the right. Reservation set: OP / NA / OO / OG on active child units (same canonical occupancy set as the headline Occupancy % tile)."
                           tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                    </h3>
                    <!-- Legend — swatch ↔ bucket ↔ threshold so the heat strip is
                         self-explanatory at a glance. Mirrors the bucket boundaries
                         in occBucket() in OperationsDashboardBL.java. -->
                    <span class="heat-legend">
                        <span class="legend-item"><span class="legend-swatch heat-legend-low"></span>Low &lt; 40 %</span>
                        <span class="legend-item"><span class="legend-swatch heat-legend-medium"></span>Medium 40 – 69 %</span>
                        <span class="legend-item"><span class="legend-swatch heat-legend-high"></span>High ≥ 70 %</span>
                    </span>
                </div>
                <div class="heat-strip">
                    <div class="heat-row" *ngFor="let row of data.futureOccupancy">
                        <div class="heat-label">{{ row.locationName }}</div>
                        <div class="heat-cells">
                            <!-- Cell renders the integer percentage inline. Clicking
                                 the cell opens /reservations filtered to stays
                                 overlapping that (date, location) — manager works the
                                 canonical reservation list instead of a proprietary
                                 drilldown. White text + soft shadow stays readable
                                 across all three bucket colours. -->
                            <div class="heat-cell heat-cell--clickable"
                                 *ngFor="let cell of row.days"
                                 [ngClass]="'bucket-' + cell.bucket"
                                 [title]="cell.date + ': ' + cell.occupancyPct + '% — click to open reservations'"
                                 (click)="openReservationsForOccupancyOnDate(cell.date, row.locationId)">{{ cell.occupancyPct }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ============================== §6b Future Occupancy Report (full dataset) ============================== -->
        <section class="ops-section" *ngIf="data.futureOccupancyReport">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>Future Occupancy Report</h3>
                    <span class="caption">Mirrors <a routerLink="/future-occupancy-report" target="_blank" rel="noopener">/future-occupancy-report</a> for the selected range</span>
                </div>

                <!-- Top KPIs -->
                <div class="fo-top-kpis">
                    <div class="kbma-kpi kbma-kpi--info">
                        <div class="kbma-kpi__label">Total Reservations</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.futureOccupancyReport?.topKpis?.totalReservations) }}</span></div>
                    </div>
                    <div class="kbma-kpi kbma-kpi--info">
                        <div class="kbma-kpi__label">Booked Nights</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.futureOccupancyReport?.topKpis?.bookedNights) }}</span></div>
                    </div>
                    <div class="kbma-kpi kbma-kpi--success">
                        <div class="kbma-kpi__label">Avg Occupancy</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtPct(data.futureOccupancyReport?.topKpis?.avgOccupancyPct) }}</span></div>
                    </div>
                </div>

                <!-- Overview / Reservations tabs -->
                <div class="fo-tabs">
                    <button class="chip" [class.chip--active]="foTab === 'overview'"     (click)="foSwitchTab('overview')">Overview</button>
                    <button class="chip" [class.chip--active]="foTab === 'reservations'" (click)="foSwitchTab('reservations')">
                        Reservations <span class="numeric">({{ fmtNum(data.futureOccupancyReport?.reservations?.length) }})</span>
                    </button>
                </div>

                <!-- Overview tab -->
                <ng-container *ngIf="foTab === 'overview'">
                    <div class="fo-overview-grid">
                        <div class="fo-card">
                            <div class="fo-card__header"><h4>Occupancy Rate (%) by Month</h4></div>
                            <div class="chart-wrap"><canvas #foMonthlyBar></canvas></div>
                        </div>
                        <div class="fo-card">
                            <div class="fo-card__header"><h4>Occupancy Share by Type</h4></div>
                            <div class="fo-pie-grid">
                                <div class="chart-wrap pie"><canvas #foTypePie></canvas></div>
                                <table class="kbma-table compact" *ngIf="data.futureOccupancyReport?.byTypeData?.length">
                                    <thead><tr><th>Type</th><th class="numeric"># of Nights</th></tr></thead>
                                    <tbody>
                                        <tr *ngFor="let t of data.futureOccupancyReport.byTypeData">
                                            <td>{{ t.resTypeName }}</td>
                                            <td class="numeric"><span class="numeric">{{ fmtNum(t.numNights) }}</span></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="fo-card wide">
                            <div class="fo-card__header"><h4>Check-ins &amp; Check-outs</h4></div>
                            <div class="caption">Daily check-in / check-out counts within selected date range</div>
                            <div class="chart-wrap"><canvas #foCheckLine></canvas></div>
                        </div>
                        <div class="fo-card">
                            <div class="fo-card__header"><h4>Check-ins by Unit Type</h4></div>
                            <div class="caption">Daily check-in volume by bedroom category</div>
                            <div class="chart-wrap"><canvas #foCheckInByBed></canvas></div>
                        </div>
                        <div class="fo-card">
                            <div class="fo-card__header"><h4>Check-outs by Unit Type</h4></div>
                            <div class="caption">Daily check-out volume by bedroom category</div>
                            <div class="chart-wrap"><canvas #foCheckOutByBed></canvas></div>
                        </div>
                    </div>
                </ng-container>

                <!-- Reservations tab -->
                <ng-container *ngIf="foTab === 'reservations'">
                    <table class="kbma-table fo-res-table" *ngIf="data.futureOccupancyReport?.reservations?.length">
                        <thead>
                            <tr>
                                <th>
                                    Res ID
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Reservation ID. Click to open the reservation / order in a new tab."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th>
                                    Property
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Short-term-rental property code. Click to open the property edit page in a new tab."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th>
                                    Guest
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Guest first + last name from the reservation profile."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th class="numeric">
                                    Beds
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Number of bedrooms in the unit (ShortTermRentalVO.numBedrooms)."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th>
                                    Check-in
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Reservation check-in calendar date (the day the guest arrives)."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th>
                                    Check-out
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Reservation check-out calendar date (the day the guest departs)."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th class="numeric">
                                    Nights
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Total nights of the reservation (checkOut − checkIn). The full stay length, regardless of the dashboard's window."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th class="numeric">
                                    In-Range
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Nights of this reservation that fall INSIDE the dashboard's selected date range. The full stay may extend before or after the range — In-Range ≤ Nights."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th class="numeric">
                                    Nightly Rate
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Average nightly rate for this reservation — room revenue ÷ Nights. Uses the same line-total formula as ADR (LEAST(adj, list) × quantity, falling back to listPrice when adj is null/0)."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th class="numeric">
                                    Revenue
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Sum of line totals across all order_detail rows for this reservation (room rate + housekeeping + fees, before tax). Excludes cancelled-line items."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th>
                                    Status
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Reservation status: OP (Reserved) · OC (Sale Closed) · OG (Owner Guest) · OO (Owner Occupied) · NA (Maintenance) · QH (Hold) · OX (Cancelled)."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                                <th>
                                    Source
                                    <i class="pi pi-info-circle th-info"
                                       pTooltip="Booking channel (OTA or direct). Resolved from r.referralSourceId via ConstantService.refSrcImages — same lookup used by the Sales / Upsell dashboards, so the icon for, e.g., Airbnb is identical across screens. Hover the icon for the channel name."
                                       tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let r of data.futureOccupancyReport.reservations">
                                <td>
                                    <a *ngIf="r.orderId != null; else resIdPlain"
                                       class="order-link"
                                       [href]="orderUrl(r.orderId)"
                                       target="_blank"
                                       rel="noopener noreferrer">{{ r.reservationId }}</a>
                                    <ng-template #resIdPlain><span class="numeric">{{ r.reservationId }}</span></ng-template>
                                </td>
                                <td>{{ r.propertyCode }}</td>
                                <td>{{ r.guestName || '—' }}</td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(r.numBedrooms) }}</span></td>
                                <td><span class="numeric">{{ r.checkInDate | date:'MM/dd/yyyy' }}</span></td>
                                <td><span class="numeric">{{ r.checkOutDate | date:'MM/dd/yyyy' }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(r.numNights) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtNum(r.clampedNights) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtCurrency(r.nightlyRatePNT) }}</span></td>
                                <td class="numeric"><span class="numeric">{{ fmtCurrency(r.revRoomRateNetTTL) }}</span></td>
                                <!-- Status cell coloured to match the /reservations
                                     list — see reservationStatusColor() helper. -->
                                <td [style.background-color]="reservationStatusColor(r.reservationStatus)">{{ reservationStatusName(r.reservationStatus) }}</td>
                                <!-- OTA / direct source icon. Same lookup as
                                     ConstantService.refSrcImages (Sales / Upsell dashboards).
                                     Hidden img when channelIcon('') so the cell stays empty
                                     for unknown / null referralSourceId. -->
                                <td>
                                    <img *ngIf="channelIcon(r.referralSourceId)"
                                         [src]="channelIcon(r.referralSourceId)"
                                         [alt]="channelLabel(r.referralSourceId)"
                                         [title]="channelLabel(r.referralSourceId)"
                                         style="height:20px; width:auto; vertical-align:middle;" />
                                    <span *ngIf="!channelIcon(r.referralSourceId)" class="caption">—</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div *ngIf="!data.futureOccupancyReport?.reservations?.length" class="empty">No reservations in range.</div>
                </ng-container>
            </div>
        </section>

        <!-- ============================== §7 Trend Charts ============================== -->
        <section class="ops-section trends-row">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>
                        Occupancy by Location — Last 30 Days
                        <i class="pi pi-info-circle th-info"
                           pTooltip="One line per location. Each X-axis tick is a DAY (M/D format, last 30 days ending today); Y-axis is that day's occupancy % for the location, computed as: distinct units booked that day with reservationStatus in OP / NA / OO / OG, divided by the location's active child-unit count. Low-inventory locations (Oahu = 1 unit, etc.) bounce between 0 % and 100 % because the denominator is tiny — that's math, not a bug."
                           tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                    </h3>
                    <span class="caption">X-axis: day; Y-axis: that day's occupancy % per location.</span>
                </div>
                <div class="chart-wrap large"><canvas #occLine></canvas></div>
            </div>

            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>
                        Bedding Demand — Last 90 Days
                        <i class="pi pi-info-circle th-info"
                           pTooltip="Daily count of futon + rollaway line items added across all orders (by orderProductId). X-axis tick = one day (M/D), trailing 90 days. Y-axis = total units of that bedding type sold that day."
                           tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                    </h3>
                    <span class="caption">X-axis: day; Y-axis: equipment line items sold that day.</span>
                </div>
                <div class="chart-wrap"><canvas #beddingLine></canvas></div>
            </div>

            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>
                        Cancellations — Last 30 Days
                        <i class="pi pi-info-circle th-info"
                           pTooltip="Number of reservations whose r.cancelDate falls on each day, last 30 days ending today. X-axis tick = M/D. Y-axis = count of cancellations on that day. A single oversized bar (e.g. 800+) almost always indicates a one-off bulk event — a data backfill, mass-cancellation campaign, or storm — rather than 800 individual guests cancelling that day. Drill into the date using the cancellations drilldown or a direct SQL on str_reservation to confirm."
                           tooltipPosition="top" tooltipStyleClass="ops-tooltip"></i>
                    </h3>
                    <span class="caption">X-axis: day cancellation was recorded (cancelDate); Y-axis: # of reservations cancelled.</span>
                </div>
                <div class="chart-wrap"><canvas #cancelBar></canvas></div>
            </div>
        </section>

        <!-- ============================== §8 Revenue & Demand Signals ============================== -->
        <section class="ops-section revenue-row">
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>Pickup (bookings made)</h3>
                    <i class="pi pi-info-circle ops-info"
                       pTooltip="Booking pace — count of confirmed reservations whose order was created (str_order.saleDate) inside each window. Status IN ('OP','OC') so cancelled bookings (OX) and on-hold bookings (QH) are excluded. NOT vehicle pickup — KBM has no vehicle pickup product. Click any cell to see the underlying reservations: Order, Booked date, Channel, Property, Guest first/last name, Check-in datetime, Status."
                       tooltipPosition="top"></i>
                </div>
                <div class="pickup-stats">
                    <div class="pickup-stat clickable"
                         (click)="openReservationsForBookings('range')"
                         pTooltip="Confirmed bookings created within the dashboard's selected date range (startDate..endDate inclusive). Click to view rows."
                         tooltipPosition="top">
                        <div class="kbma-kpi__label">In Range</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.revenueSignals?.pickup?.range) }}</span></div>
                    </div>
                    <div class="pickup-stat clickable"
                         (click)="openReservationsForBookings('today')"
                         pTooltip="Confirmed bookings created on the dashboard's endDate. Click to view rows."
                         tooltipPosition="top">
                        <div class="kbma-kpi__label">Today</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.revenueSignals?.pickup?.today) }}</span></div>
                    </div>
                    <div class="pickup-stat clickable"
                         (click)="openReservationsForBookings('yesterday')"
                         pTooltip="Confirmed bookings created on endDate − 1 day. Click to view rows."
                         tooltipPosition="top">
                        <div class="kbma-kpi__label">Yesterday</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.revenueSignals?.pickup?.yesterday) }}</span></div>
                    </div>
                    <div class="pickup-stat clickable"
                         (click)="openReservationsForBookings('last7d')"
                         pTooltip="Confirmed bookings created in the rolling 7-day window [endDate − 7 .. endDate − 1] — excludes today so you can compare today vs the prior week. Click to view rows."
                         tooltipPosition="top">
                        <div class="kbma-kpi__label">Last 7d</div>
                        <div class="kbma-kpi__value sm"><span class="numeric">{{ fmtNum(data.revenueSignals?.pickup?.last7d) }}</span></div>
                    </div>
                </div>
            </div>

            <div class="ops-card">
                <div class="ops-card__header"><h3>Cancellations</h3></div>
                <div class="cancel-stats">
                    <div class="cancel-stat clickable" (click)="openReservationsForCancellations()">
                        <div class="kbma-kpi__label">Count</div>
                        <div class="kbma-kpi__value"><span class="numeric">{{ fmtNum(data.revenueSignals?.cancellations?.countInRange) }}</span></div>
                    </div>
                    <div class="cancel-stat">
                        <div class="kbma-kpi__label">Revenue lost</div>
                        <div class="kbma-kpi__value"><span class="numeric">{{ fmtCurrency(data.revenueSignals?.cancellations?.revenueLostInRange) }}</span></div>
                    </div>
                </div>
            </div>

            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>Channel Mix — Today's Arrivals</h3>
                    <span class="kbma-badge kbma-badge--info channel-mix-scope-badge">Today only</span>
                    <i class="pi pi-info-circle ops-info"
                       pTooltip="Distribution of TODAY'S arrivals (endDate only — NOT the dashboard's full selected range) by referral source. Uses COALESCE(reservation.referralSourceId, order.referralSourceId) so VRBO / Airbnb / Booking.com / Expedia / Marriott bookings are correctly attributed even when the channel field is only set at the order level. Null → 'Direct' (phone-in / kbmresorts.com / corporate). Status filter: OP/OC/OG/OO (active). Range-scoped channel trends live in the separate Bookings Trend section."
                       tooltipPosition="top"></i>
                </div>
                <div class="chart-wrap"><canvas #channelDonut></canvas></div>
            </div>

            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>Upsell Revenue — 30d</h3>
                    <i class="pi pi-info-circle ops-info"
                       pTooltip="Sum of order_detail line totals over the trailing 30 days for four non-room categories: resort fee, damage waiver, futon, rollaway. Uses the LEAST(adj, list) × qty line-total formula with fallback to listPrice — same shape as room revenue. Pet fees are excluded (KBM tracks pet acceptance on the reservation, not as a line item, so historical pet-fee rows are noise)."
                       tooltipPosition="top"></i>
                </div>
                <div class="kbma-kpi__value">
                    <span class="numeric">{{ fmtCurrency(data.revenueSignals?.upsellRevenue30d?.total) }}</span>
                </div>
                <div class="upsell-breakdown">
                    <div class="upsell-row">
                        <span class="upsell-label">Resort fee</span>
                        <span class="numeric">{{ fmtCurrency(data.revenueSignals?.upsellRevenue30d?.resortFee) }}</span>
                    </div>
                    <div class="upsell-row">
                        <span class="upsell-label">Damage waiver</span>
                        <span class="numeric">{{ fmtCurrency(data.revenueSignals?.upsellRevenue30d?.damageWaiver) }}</span>
                    </div>
                    <div class="upsell-row">
                        <span class="upsell-label">Futon</span>
                        <span class="numeric">{{ fmtCurrency(data.revenueSignals?.upsellRevenue30d?.futon) }}</span>
                    </div>
                    <div class="upsell-row">
                        <span class="upsell-label">Rollaway</span>
                        <span class="numeric">{{ fmtCurrency(data.revenueSignals?.upsellRevenue30d?.rollaway) }}</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- §10 DOGE Operations Initiatives — removed per manager request.
             Backend buildDoge / 'doge' section also dropped so we don't keep
             paying for a query whose output never renders. -->

        <!-- ============================== §11 Guest Experience ============================== -->
        <section class="ops-section guest-row" *ngIf="data.guestExperience">
            <div class="ops-card">
                <div class="ops-card__header"><h3>Repeat Guest Ratio</h3></div>
                <div class="kbma-kpi__value"><span class="numeric">{{ fmtPct(data.guestExperience?.repeatGuestRatio) }}</span></div>
                <div class="caption">last 90 days</div>
            </div>
            <div class="ops-card">
                <div class="ops-card__header">
                    <h3>Review Score — Recent Days</h3>
                    <i class="pi pi-info-circle ops-info"
                       pTooltip="Daily aggregated review scores for the trailing 30 days ending on the dashboard's endDate. Source: str_review (VRBO + KBM portal reviews — denied reviews excluded). Click any row to drill into THAT day's reviews; click 'View all 30d' below the table to see every review in the window. Columns in the drilldown: Date · Property · Reviewer · Rating · Source · Title · Review."
                       tooltipPosition="top"></i>
                </div>
                <table class="kbma-table review-trend-table" *ngIf="data.guestExperience?.reviewScoreTrend30d?.length">
                    <thead><tr><th>Date</th><th class="numeric">Avg</th><th class="numeric"># Reviews</th></tr></thead>
                    <tbody>
                        <tr *ngFor="let r of data.guestExperience.reviewScoreTrend30d"
                            class="clickable"
                            (click)="reviewsDrilldown(r.date)"
                            pTooltip="Click to see reviews posted on this day"
                            tooltipPosition="left">
                            <td><span class="numeric">{{ r.date | date:'MM/dd' }}</span></td>
                            <td class="numeric"><span class="numeric">{{ r.avgScore != null ? r.avgScore : '—' }}</span></td>
                            <td class="numeric"><span class="numeric">{{ fmtNum(r.reviewCount) }}</span></td>
                        </tr>
                    </tbody>
                </table>
                <div *ngIf="data.guestExperience?.reviewScoreTrend30d?.length"
                     class="review-trend-footer">
                    <a href="javascript:void(0)" (click)="reviewsDrilldown()">View all 30d →</a>
                </div>
                <div *ngIf="!data.guestExperience?.reviewScoreTrend30d?.length" class="empty">—</div>
            </div>
        </section>

        <!-- ============================== §9 Quick Actions ============================== -->
        <section class="ops-section quick-actions">
            <button class="kbma-btn kbma-btn--secondary" (click)="openArrivalsAgent()">Arrivals Agent</button>
            <button class="kbma-btn kbma-btn--secondary" (click)="openFutureOccupancy()">Future Occupancy</button>
            <button class="kbma-btn kbma-btn--secondary" (click)="openVacancyReport()">Vacancy Report</button>
            <button class="kbma-btn kbma-btn--secondary" (click)="openOwnerStatements()">Owner Statements</button>
        </section>

    </ng-container>

</div>
